/**
 * Utility functions for calculating passenger-wise amounts for SSR selections
 */

export interface PassengerAmounts {
  [passengerIndex: number]: {
    seat: number;
    meal: number;
    baggage: number;
    total: number;
  };
}

export interface SSRItem {
  seat?: {
    amount: number;
    active?: boolean;
  };
  meal?: {
    amount: number;
    count: number;
  };
  baggage?: {
    amount: number;
    count: number;
  };
}

/**
 * Calculate passenger-wise amounts from selected SSR data
 */
export const calculatePassengerAmounts = (
  selectedSSR: any[],
): PassengerAmounts => {
  const passengerAmounts: PassengerAmounts = {};

  if (!selectedSSR || selectedSSR.length === 0) {
    return passengerAmounts;
  }

  selectedSSR.forEach((passengerData, passengerIndex) => {
    let seatAmount = 0;
    let mealAmount = 0;
    let baggageAmount = 0;

    // Iterate through flights and segments for this passenger
    passengerData.forEach((flight: any) => {
      flight.forEach((segment: SSRItem) => {
        // Calculate seat amount
        if (segment.seat?.active && segment.seat?.amount) {
          seatAmount += segment.seat.amount;
        }

        // Calculate meal amount
        if (segment.meal?.count && segment.meal?.amount) {
          mealAmount += segment.meal.count * segment.meal.amount;
        }

        // Calculate baggage amount
        if (segment.baggage?.count && segment.baggage?.amount) {
          baggageAmount += segment.baggage.count * segment.baggage.amount;
        }
      });
    });

    passengerAmounts[passengerIndex] = {
      seat: seatAmount,
      meal: mealAmount,
      baggage: baggageAmount,
      total: seatAmount + mealAmount + baggageAmount,
    };
  });

  return passengerAmounts;
};

/**
 * Get total amounts across all passengers
 */
export const getTotalAmounts = (passengerAmounts: PassengerAmounts) => {
  const totals = {
    seat: 0,
    meal: 0,
    baggage: 0,
    total: 0,
  };

  Object.values(passengerAmounts).forEach((amounts) => {
    totals.seat += amounts.seat;
    totals.meal += amounts.meal;
    totals.baggage += amounts.baggage;
    totals.total += amounts.total;
  });

  return totals;
};

/**
 * Format passenger name for display
 */
export const formatPassengerName = (passenger: any): string => {
  if (!passenger) return 'Unknown Passenger';

  const { title, firstName, lastName } = passenger;
  return `${title || ''} ${firstName || ''} ${lastName || ''}`.trim();
};

/**
 * Get passenger-wise breakdown for display
 */
export const getPassengerBreakdown = (
  selectedSSR: any[],
  passengerData: any[],
): Array<{
  name: string;
  amounts: {
    seat: number;
    meal: number;
    baggage: number;
    total: number;
  };
}> => {
  const passengerAmounts = calculatePassengerAmounts(selectedSSR);

  return passengerData.map((passenger, index) => ({
    name: formatPassengerName(passenger),
    amounts: passengerAmounts[index] || {
      seat: 0,
      meal: 0,
      baggage: 0,
      total: 0,
    },
  }));
};

/**
 * Extract SSR items from selectedSSR structure and populate passenger-level arrays
 */
export const extractPassengerSSRItems = (
  selectedSSR: any[],
): {
  seat: any[];
  meal: any[];
  baggage: any[];
}[] => {
  if (!selectedSSR || selectedSSR.length === 0) {
    return [];
  }

  return selectedSSR.map((passengerData) => {
    const seatItems: any[] = [];
    const mealItems: any[] = [];
    const baggageItems: any[] = [];

    // Iterate through flights and segments for this passenger
    passengerData.forEach((flight: any) => {
      flight.forEach((segment: any) => {
        // Extract seat items
        if (
          segment.seat &&
          Object.keys(segment.seat).length > 0 &&
          segment.seat.active
        ) {
          seatItems.push({
            ...segment.seat,
            active: true,
          });
        }

        // Extract meal items
        if (
          segment.meal &&
          Object.keys(segment.meal).length > 0 &&
          segment.meal.count > 0
        ) {
          // Add multiple items based on count
          for (let i = 0; i < segment.meal.count; i++) {
            mealItems.push({
              ...segment.meal,
              quantity: 1,
            });
          }
        }

        // Extract baggage items
        if (
          segment.baggage &&
          Object.keys(segment.baggage).length > 0 &&
          segment.baggage.count > 0
        ) {
          // Add multiple items based on count
          for (let i = 0; i < segment.baggage.count; i++) {
            baggageItems.push({
              ...segment.baggage,
            });
          }
        }
      });
    });

    return {
      seat: seatItems,
      meal: mealItems,
      baggage: baggageItems,
    };
  });
};
